import { Card, Subtitle2, Text } from "@fluentui/react-components";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import { AppSpinner } from "components/AppSpinner/AppSpinner";
import { useCurrencyRates } from "hooks/useCurrencyRates";
import useGetAllCurrencyOptions from "hooks/useGetAllCurrencyOptions";
import useGetCurrencyOptionsPerUser from "hooks/useGetCurrencyOptionsPerUser";
import { useAtomValue } from "jotai";
import { allCurrenciesOption, devices } from "library/constants";
import { ICompanyInfosInfo, SelectItem } from "library/types";
import { formatNumber } from "library/utils";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useGetCompanyInfos } from "state/queries/useGetCompanyInfos";
import { useGetTaxonomies } from "state/queries/useGetTaxonomies";
import { selectedCompanyState } from "store/UIHrPage";
import {
  filterCurrencyAtom,
  filterPaymentMethodAtom,
  filterPaymentTypeAtom,
  historicReportAtom,
  periodFromDateAtom,
  periodToDateAtom,
  reportDateAtom,
  selectedBenefitsAtom,
} from "store/UIHrPageDashboard";
import styled from "styled-components";
import FilterSection from "./FilterSection/FilterSection";
import { formattedNumber, vibrantColors } from "./utils";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

export const Summary = () => {
  const { t } = useTranslation();
  const selectedCompany = useAtomValue(selectedCompanyState);
  const [filteredCompanyInfo, setFilteredCompanyInfo] =
    useState<ICompanyInfosInfo | null>(null);
  const reportDate = useAtomValue(reportDateAtom);
  const historicReport = useAtomValue(historicReportAtom);
  const selectedBenefits = useAtomValue(selectedBenefitsAtom);
  const filterPaymentMethod = useAtomValue(filterPaymentMethodAtom);
  const filterPaymentType = useAtomValue(filterPaymentTypeAtom);
  const filterCurrency = useAtomValue(filterCurrencyAtom);

  const { currencyRatesPerCountries } = useCurrencyRates();

  const [selectedCurrencyToConvert, setSelectedCurrencyToConvert] =
    useState<SelectItem | null>(null);

  const [convertCurrencyDisable, setConvertCurrencyDisabled] = useState(false);
  const { data: companyInfoData, isFetching: loadingInfo } = useGetCompanyInfos(
    {
      countryCode: selectedCompany?.countryCode || null,
      clientInternalNumberGOS: selectedCompany?.clientInternalNumberGos || "",
      onDate: historicReport ? moment(reportDate).format("YYYY-MM-DD") : "now",
      enabled: !!selectedCompany,
    },
  );

  const { defaultCurrencyByUserCountry } = useGetCurrencyOptionsPerUser({
    countryCode: selectedCompany?.countryCode,
  });


  const [companyInfo, setCompanyInfo] = useState<ICompanyInfosInfo | undefined>(
    undefined,
  );

  const allCurrencySelected =
    filterCurrency?.value === allCurrenciesOption.value;

  const periodFromDate = useAtomValue(periodFromDateAtom);
  const periodToDate = useAtomValue(periodToDateAtom);
  const allCurrencyOptions = useGetAllCurrencyOptions();
  const { data: taxonomies } = useGetTaxonomies();

  const convertPrice = useCallback(
    ({ price, currencyCode }) => {
      const numericPrice = Number(price) || 0;

      if (allCurrencySelected && !selectedCurrencyToConvert?.codeShort) {
        //convert to default currency of user country when all currencies selected and no specific currency to convert selected
        const rate = currencyRatesPerCountries[String(currencyCode)]?.rate;

        // If the source currency has no known rate (or rate is 0), we can't
        // convert — fall back to the raw price so the totals still include
        // this value instead of poisoning the sum with NaN/Infinity.
        if (!rate) {
          return numericPrice;
        }

        const priceInEur = numericPrice / rate;
        const priceForDefaultCountryCurrency =
          priceInEur *
          Number(
            currencyRatesPerCountries[defaultCurrencyByUserCountry?.value]
              ?.rate || 1,
          );

        return priceForDefaultCountryCurrency;
      }
      //otherwise return price
      return numericPrice;
    },
    [
      currencyRatesPerCountries,
      allCurrencySelected,
      selectedCurrencyToConvert,
      defaultCurrencyByUserCountry,
    ],
  );

  useEffect(() => {
    if (companyInfoData) {
      setCompanyInfo(companyInfoData);
    }
  }, [companyInfoData]);

  useEffect(() => {
    if (!companyInfo) return;
    /**
     * Filters benefit implementations based on the selected currency.
     * If the "all currencies" option is selected, returns all implementations without filtering.
     * Otherwise, filters implementations to only include those matching the selected currency code.
     * Maps the filtered implementations to extract their benefit implementation tags.
     */
    const implementationsWithGivenCurrency = companyInfo?.benefitImplementations
      ?.filter((imp) =>
        allCurrencySelected
          ? true
          : imp.currencyCode + "" === filterCurrency?.value + "",
      )
      .map((imp) => imp.benefitImplementationTag);

    const selectedCategoryTags = Array.from(selectedBenefits)
      .filter((b) => b.includes("_package"))
      .map((p, index) => {
        const categoryTag = companyInfo?.benefitPackages?.find(
          (bp) =>
            bp.benefitPackageTag + "" === (p + "").replaceAll("_package", ""),
        )?.benefitCategoryTag;
        return categoryTag;
      });
    // make list of unique category tags
    const uniqueCategoryTags = Array.from(new Set(selectedCategoryTags));
    // make list of categries based on unique category tags
    const selectedCategories = companyInfo?.benefitCategories?.filter((bc) =>
      uniqueCategoryTags.includes(bc.benefitCategoryTag),
    );

    let filteredEmployeeEnrollments = companyInfo?.employeeEnrollments
      ?.filter((en) =>
        Array.from(selectedBenefits).includes(
          en.benefitPackageTag + "_package",
        ),
      )
      .filter((en) => {
        return en.dateOfEnrollment !== null;
      })
      .filter((en) => {
        return implementationsWithGivenCurrency?.includes(
          (en as any).benefitImplementationTag,
        );
      })
      .filter(
        (en) =>
          filterPaymentMethod?.value === "" ||
          en.paymentMethod === filterPaymentMethod?.value,
      )
      .filter(
        (en) =>
          filterPaymentType?.value === "" ||
          en.paymentType === filterPaymentType?.value,
      )
      .map((en) => {
        const dol = en.dateOfEligibility
          ? new Date(en.dateOfEligibility)
          : null;
        const doe = en.dateOfEnrollment ? new Date(en.dateOfEnrollment) : null;
        const doc = en.dateOfCancellation
          ? new Date(en.dateOfCancellation)
          : null;
        const ps = new Date(periodFromDate);
        const pe = new Date(periodToDate);

        const isNewlyEligible = dol ? dol >= ps && dol <= periodToDate : false;
        const isNewlyEnrolled = doe ? doe >= ps && doe <= periodToDate : false;
        const isNewlyCancelled = doc ? doc >= ps && doc <= periodToDate : false;

        const isActiveInPeriod = doe
          ? (doe < ps &&
              (doc === null || doc >= ps) &&
              (doc === null || doc < pe)) ||
            (doe >= ps && doe < pe && (doc === null || doc >= pe)) ||
            (doe >= ps && (doc === null || doc < pe)) ||
            (doe < ps && (doc === null || doc >= pe))
          : false;

        const paymentMethodCode = getPaymentMethodCode(
          en.paymentMethod,
          taxonomies,
        );
        const matchingImplementation =
          companyInfo?.benefitImplementations?.find(
            (imp) =>
              imp.benefitImplementationTag ===
              (en as any).benefitImplementationTag,
          );

        const implValidTo = matchingImplementation?.validTo
          ? new Date(matchingImplementation.validTo)
          : null;

        // Use whichever comes first: cancellation date or implementation validTo
        const effectiveEndDate =
          doc && implValidTo
            ? doc < implValidTo
              ? doc
              : implValidTo
            : (doc ?? implValidTo);

        const installmentCount = countInstallmentsInPeriod(
          doe,
          effectiveEndDate,
          ps,
          pe,
          paymentMethodCode,
        );

        const currencyCode = matchingImplementation?.currencyCode ?? null;

        return {
          ...en,
          installmentCount,
          isNewlyEligible,
          isNewlyEnrolled,
          isNewlyCancelled,
          isActiveInPeriod,
          currencyCode,
        };
      });

    let filteredEmployeeEligibilities = companyInfo?.employeeEnrollments
      ?.filter((en) =>
        Array.from(selectedBenefits).includes(
          en.benefitPackageTag + "_package",
        ),
      )
      .filter((en) => {
        return en.dateOfEnrollment === null;
      })
      .filter((en) => {
        return implementationsWithGivenCurrency?.includes(
          (en as any).benefitImplementationTag,
        );
      })
      .filter(
        (en) =>
          filterPaymentMethod?.value === "" ||
          en.paymentMethod === filterPaymentMethod?.value,
      )
      .filter(
        (en) =>
          filterPaymentType?.value === "" ||
          en.paymentType === filterPaymentType?.value,
      )
      .map((en) => {
        const dol = en.dateOfEligibility
          ? new Date(en.dateOfEligibility)
          : null;
        const doe = en.dateOfEnrollment ? new Date(en.dateOfEnrollment) : null;
        const doc = en.dateOfCancellation
          ? new Date(en.dateOfCancellation)
          : null;
        const ps = new Date(periodFromDate);
        const pe = new Date(periodToDate);

        const isNewlyEligible = dol ? dol >= ps && dol <= periodToDate : false;
        const isNewlyEnrolled = doe ? doe >= ps && doe <= periodToDate : false;
        const isNewlyCancelled = doc ? doc >= ps && doc <= periodToDate : false;

        const isActiveInPeriod = doe
          ? (doe < ps &&
              (doc === null || doc >= ps) &&
              (doc === null || doc < pe)) ||
            (doe >= ps && doe < pe && (doc === null || doc >= pe)) ||
            (doe >= ps && (doc === null || doc < pe)) ||
            (doe < ps && (doc === null || doc >= pe))
          : false;

        const matchingImpl = companyInfo?.benefitImplementations?.find(
          (imp) =>
            imp.benefitImplementationTag ===
            (en as any).benefitImplementationTag,
        );

        const implValidTo = matchingImpl?.validTo
          ? new Date(matchingImpl.validTo)
          : null;

        const effectiveEndDate =
          doc && implValidTo
            ? doc < implValidTo
              ? doc
              : implValidTo
            : (doc ?? implValidTo);

        const paymentMethodCode = getPaymentMethodCode(
          en.paymentMethod,
          taxonomies,
        );
        const installmentCount = countInstallmentsInPeriod(
          doe,
          effectiveEndDate,
          ps,
          pe,
          paymentMethodCode,
        );

        return {
          ...en,
          installmentCount,
          isNewlyEligible,
          isNewlyEnrolled,
          isNewlyCancelled,
          isActiveInPeriod,
        };
      });

    const retVal = {
      ...companyInfo,
      filteredEmployeeEnrollments: filteredEmployeeEnrollments,
      companyCosts: filteredEmployeeEnrollments?.reduce((accumulator, en) => {
        const singleInstallment = en.usingPercentagesForPaidBy
          ? (en.paidByCompany / 100) *
            convertPrice({ price: en.price, currencyCode: en.currencyCode })
          : convertPrice({
              price: en.paidByCompany,
              currencyCode: en.currencyCode,
            });
        return accumulator + en.installmentCount * singleInstallment;
      }, 0),
      employeeCosts: filteredEmployeeEnrollments?.reduce((accumulator, en) => {
        const singleInstallment = en.usingPercentagesForPaidBy
          ? (en.paidByEmployee / 100) *
            convertPrice({ price: en.price, currencyCode: en.currencyCode })
          : convertPrice({
              price: en.paidByEmployee,
              currencyCode: en.currencyCode,
            });
        return accumulator + en.installmentCount * singleInstallment;
      }, 0),
      // count unique employees from enrollments (excluding dependents)
      employeeEnrollmentsCount: Array.from(
        new Set(filteredEmployeeEnrollments?.filter((en) => !en.isDependent)),
      ).length,
      totalEmployeeEnrollmentsCount:
        Array.from(
          new Set(filteredEmployeeEnrollments?.filter((en) => !en.isDependent)),
        ).length +
        Array.from(
          new Set(
            filteredEmployeeEligibilities?.filter((en) => !en.isDependent),
          ),
        ).length,

      totalEmployeesCount: companyInfo?.employees?.filter(
        (em) =>
          moment(em.dateOfEmployment).isBefore(periodToDate) &&
          (em.dateOfExit === null ||
            moment(em.dateOfExit).isAfter(periodFromDate)),
      ).length,
      // count unique dependents from enrollments
      dependentEnrollmentsCount: Array.from(
        new Set(filteredEmployeeEnrollments?.filter((en) => en.isDependent)),
      ).length,
      totalDependentEnrollmentsCount:
        Array.from(
          new Set(filteredEmployeeEnrollments?.filter((en) => en.isDependent)),
        ).length +
        Array.from(
          new Set(
            filteredEmployeeEligibilities?.filter((en) => en.isDependent),
          ),
        ).length,
      totalDependentsCount: companyInfo?.employees
        ?.filter(
          (em) =>
            moment(em.dateOfEmployment).isBefore(periodToDate) &&
            (em.dateOfExit === null ||
              moment(em.dateOfExit).isAfter(periodFromDate)),
        )
        .reduce((accumulator, employee) => {
          return (
            accumulator + (employee.dependents ? employee.dependents.length : 0)
          );
        }, 0),
      // grouped by category, implementation, package
      paidByEmployeeChartData: {
        labels: [
          t("hbh.dashboard.forEmployees.label"),
          t("hbh.dashboard.forDependents.label"),
        ],
        datasets: selectedCategories?.map((category, index) => {
          const color = vibrantColors[index];
          return {
            label: (category as any).name,
            backgroundColor: color.backgroundColor,
            borderColor: color.borderColor,
            borderWidth: 1,
            data: [
              filteredEmployeeEnrollments
                ?.filter(
                  (en) =>
                    (en as any).benefitCategoryTag ===
                      category?.benefitCategoryTag && en.dependentType === null,
                )
                ?.reduce((accumulator, en) => {
                  const singleInstallment = en.usingPercentagesForPaidBy
                    ? (en.paidByEmployee / 100) *
                      convertPrice({
                        price: en.price,
                        currencyCode: en.currencyCode,
                      })
                    : convertPrice({
                        price: en.paidByEmployee,
                        currencyCode: en.currencyCode,
                      });
                  return accumulator + en.installmentCount * singleInstallment;
                }, 0),
              filteredEmployeeEnrollments
                ?.filter(
                  (en) =>
                    (en as any).benefitCategoryTag ===
                      category?.benefitCategoryTag && en.dependentType !== null,
                )
                ?.reduce((accumulator, en) => {
                  const singleInstallment = en.usingPercentagesForPaidBy
                    ? (en.paidByEmployee / 100) *
                      convertPrice({
                        price: en.price,
                        currencyCode: en.currencyCode,
                      })
                    : convertPrice({
                        price: en.paidByEmployee,
                        currencyCode: en.currencyCode,
                      });
                  return accumulator + en.installmentCount * singleInstallment;
                }, 0),
            ],
          };
        }),
      },
      paidByCompanyChartData: {
        labels: [
          t("hbh.dashboard.forEmployees.label"),
          t("hbh.dashboard.forDependents.label"),
        ],
        datasets: selectedCategories?.map((category, index) => {
          const color = vibrantColors[index];
          return {
            label: (category as any).name,
            backgroundColor: color.backgroundColor,
            borderColor: color.borderColor,
            borderWidth: 1,
            data: [
              filteredEmployeeEnrollments
                ?.filter(
                  (en) =>
                    (en as any).benefitCategoryTag ===
                      category?.benefitCategoryTag && en.dependentType === null,
                )
                ?.reduce((accumulator, en) => {
                  const singleInstallment = en.usingPercentagesForPaidBy
                    ? (en.paidByCompany / 100) *
                      convertPrice({
                        price: en.price,
                        currencyCode: en.currencyCode,
                      })
                    : convertPrice({
                        price: en.paidByCompany,
                        currencyCode: en.currencyCode,
                      });
                  return accumulator + en.installmentCount * singleInstallment;
                }, 0),
              filteredEmployeeEnrollments
                ?.filter(
                  (en) =>
                    (en as any).benefitCategoryTag ===
                      category?.benefitCategoryTag && en.dependentType !== null,
                )
                ?.reduce((accumulator, en) => {
                  const singleInstallment = en.usingPercentagesForPaidBy
                    ? (en.paidByCompany / 100) *
                      convertPrice({
                        price: en.price,
                        currencyCode: en.currencyCode,
                      })
                    : convertPrice({
                        price: en.paidByCompany,
                        currencyCode: en.currencyCode,
                      });
                  return accumulator + en.installmentCount * singleInstallment;
                }, 0),
            ],
          };
        }),
      },
      formattedNumber: (() => {
        // Use the selected filter currency, otherwise default to EUR
        let currencyCode = "EUR";

        if (filterCurrency?.value) {
          const currencyOption = allCurrencyOptions?.find(
            (currency) => currency.value === filterCurrency.value,
          );

          if (currencyOption?.codeShort) {
            currencyCode = currencyOption.codeShort;
          }
        }

        if (allCurrencySelected && !selectedCurrencyToConvert?.codeShort) {
          return new Intl.NumberFormat("de-AT", {
            style: "currency",
            currency: defaultCurrencyByUserCountry?.codeShort || currencyCode,
          });
        }

        return new Intl.NumberFormat("de-AT", {
          style: "currency",
          currency: selectedCurrencyToConvert?.codeShort || currencyCode,
        });
      })(),
    };
    setFilteredCompanyInfo(retVal as ICompanyInfosInfo);
  }, [
    companyInfo,
    selectedBenefits,
    filterCurrency,
    filterPaymentMethod,
    filterPaymentType,
    periodFromDate,
    periodToDate,
    allCurrencyOptions,
    allCurrencySelected,
    taxonomies,
    defaultCurrencyByUserCountry?.codeShort,
    t,
  ]);

  return (
    <Container>
      <FilterSection
        defaultCurrencyByUserCountry={defaultCurrencyByUserCountry}
        companyInfo={companyInfo}
        defaultCompanyInfo={companyInfoData}
        isLoading={loadingInfo}
        setCompanyInfo={setCompanyInfo}
        convertCurrencyDisable={convertCurrencyDisable}
        setConvertCurrencyDisabled={setConvertCurrencyDisabled}
        selectedCurrencyToConvert={selectedCurrencyToConvert}
        setSelectedCurrencyToConvert={setSelectedCurrencyToConvert}
        countryCode={selectedCompany?.countryCode}
      />

      <Content>
        <ContentWrap3>
          <div>
            <Card style={{ padding: "20px" }}>
              <Subtitle2>
                {t("hbh.dashboard.employeesTotal.label")}:{" "}
                {filteredCompanyInfo?.totalEmployeesCount ?? "0"}
              </Subtitle2>
              <Subtitle2>
                {t("hbh.dashboard.dependentsTotal.label")}:{" "}
                {filteredCompanyInfo?.totalDependentsCount ?? "0"}
              </Subtitle2>
            </Card>
          </div>

          <div>
            <Card style={{ padding: "20px" }}>
              <Subtitle2>
                {t("hbh.dashboard.employeeEnrollmentsTotal.label")}:{" "}
                {filteredCompanyInfo?.employeeEnrollmentsCount ?? "0"}/
                {filteredCompanyInfo?.totalEmployeeEnrollmentsCount ?? "0"}
              </Subtitle2>
              <Subtitle2>
                {t("hbh.dashboard.dependentEnrollmentsTotal.label")}:{" "}
                {filteredCompanyInfo?.dependentEnrollmentsCount ?? "0"}/
                {filteredCompanyInfo?.totalDependentEnrollmentsCount ?? "0"}
              </Subtitle2>
            </Card>
          </div>
          <div>
            <Card style={{ padding: "20px" }}>
              <Subtitle2>
                {t("hbh.dashboard.companyCosts.label")}:{" "}
                {Number.isFinite(filteredCompanyInfo?.companyCosts)
                  ? (
                      filteredCompanyInfo?.formattedNumber ?? formattedNumber
                    ).format(filteredCompanyInfo?.companyCosts as number)
                  : null}
              </Subtitle2>
              <Subtitle2>
                {t("hbh.dashboard.employeeCosts.label")}:{" "}
                {Number.isFinite(filteredCompanyInfo?.employeeCosts)
                  ? (
                      filteredCompanyInfo?.formattedNumber ?? formattedNumber
                    ).format(filteredCompanyInfo?.employeeCosts as number)
                  : null}
              </Subtitle2>
            </Card>
          </div>
        </ContentWrap3>

        <ContentWrap2>
          <Card style={{ padding: "20px" }}>
            <Text>{t("hbh.dashboard.paidByEmployee.label")}</Text>
            {loadingInfo || !filteredCompanyInfo ? (
              <div
                style={{
                  height: `calc(100vh - ${600 - 44}px)`,
                  overflowY: "scroll",
                }}
              />
            ) : (
              <Bar
                data={{
                  labels:
                    filteredCompanyInfo?.paidByEmployeeChartData?.labels ?? [],
                  datasets:
                    filteredCompanyInfo?.paidByEmployeeChartData?.datasets ??
                    [],
                }}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { position: "top" },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          let label = (() => {
                            if (selectedCurrencyToConvert?.codeShort) {
                              return selectedCurrencyToConvert.codeShort;
                            } else if (allCurrencySelected) {
                              return defaultCurrencyByUserCountry?.codeShort;
                            } else {
                              return context.dataset.label;
                            }
                          })();

                          if (label) {
                            label += ": ";
                          }
                          if (context.parsed.y !== null) {
                            label += formatNumber(context.parsed.y);
                          }
                          return label;
                        },
                      },
                    },
                  },
                }}
              />
            )}
          </Card>
          <Card style={{ padding: "20px" }}>
            <Text>{t("hbh.dashboard.paidByCompany.label")}</Text>
            {loadingInfo || !filteredCompanyInfo ? (
              <div
                style={{
                  height: `calc(100vh - ${600 - 44}px)`,
                  overflowY: "scroll",
                }}
              />
            ) : (
              <Bar
                data={{
                  labels:
                    filteredCompanyInfo?.paidByCompanyChartData?.labels ?? [],
                  datasets:
                    filteredCompanyInfo?.paidByCompanyChartData?.datasets ?? [],
                }}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { position: "top" },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          let label = (() => {
                            if (selectedCurrencyToConvert?.codeShort) {
                              return selectedCurrencyToConvert.codeShort;
                            } else if (allCurrencySelected) {
                              return defaultCurrencyByUserCountry?.codeShort;
                            } else {
                              return context.dataset.label;
                            }
                          })();
                          if (label) {
                            label += ": ";
                          }
                          if (context.parsed.y !== null) {
                            label += formatNumber(context.parsed.y);
                          }
                          return label;
                        },
                      },
                    },
                  },
                }}
              />
            )}
          </Card>
        </ContentWrap2>
      </Content>
      {loadingInfo && <AppSpinner />}
    </Container>
  );
};

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  padding: 20px;

  @media only screen and ${devices.md} {
    grid-template-columns: 1fr 3fr;
  }
`;
const Content = styled.div`
  display: grid;
  grid-template-rows: 1fr;
  gap: 20px;

  @media only screen and ${devices.md} {
    grid-template-rows: auto 1fr;
  }
`;

const ContentWrap3 = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media only screen and ${devices.md} {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

const ContentWrap2 = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media only screen and ${devices.md} {
    grid-template-columns: 1fr 1fr;
  }
`;
/**
 * Resolve a numeric paymentMethod taxonomy ID to its code string.
 * e.g. 834 → "PaymentMethod.MONTHLY"
 */
function getPaymentMethodCode(
  paymentMethodId: number,
  taxonomies: any,
): string {
  if (!taxonomies?.PaymentMethod) return "";
  const entry = taxonomies.PaymentMethod.find(
    (t: any) => t.id === paymentMethodId,
  );
  return entry?.code ?? "";
}

/**
 * Return the number of months between installments for a given payment method.
 * Returns 0 for one-time payments (single payment at enrollment).
 */
function getInstallmentIntervalMonths(paymentMethodCode: string): number {
  switch (paymentMethodCode) {
    case "PaymentMethod.MONTHLY":
      return 1;
    case "PaymentMethod.QUARTERLY":
      return 3;
    case "PaymentMethod.SEMESTERLY":
      return 6;
    case "PaymentMethod.ANNUALLY":
      return 12;
    case "PaymentMethod.ONE_TIME":
    default:
      return 0;
  }
}

/**
 * Count how many installment payment dates fall within [periodStart, periodEnd)
 * for a given enrollment.
 *
 * - ONE_TIME: paid once at enrollment date. Counted if enrollment date is in period.
 * - ANNUALLY: paid once per year starting from enrollment date. Counted per anniversary in period.
 * - MONTHLY / QUARTERLY / SEMESTERLY: paid every N months from enrollment date.
 *   Each installment date that falls within the period (and before cancellation) is counted.
 */
function countInstallmentsInPeriod(
  enrollmentDate: Date | null,
  cancellationDate: Date | null,
  periodStart: Date,
  periodEnd: Date,
  paymentMethodCode: string,
): number {
  if (!enrollmentDate) return 0;

  const intervalMonths = getInstallmentIntervalMonths(paymentMethodCode);

  // One-time: single payment at enrollment
  if (intervalMonths === 0) {
    if (
      enrollmentDate >= periodStart &&
      enrollmentDate < periodEnd &&
      (!cancellationDate || enrollmentDate < cancellationDate)
    ) {
      return 1;
    }
    return 0;
  }

  // Recurring: generate installment dates from enrollment forward
  let count = 0;
  const current = new Date(enrollmentDate);

  // Advance to the first installment date that could be >= periodStart
  while (current < periodStart) {
    current.setMonth(current.getMonth() + intervalMonths);
  }

  // Count installments within [periodStart, periodEnd)
  while (current < periodEnd) {
    // Stop if cancelled before this installment
    if (cancellationDate && current >= cancellationDate) break;
    count++;
    current.setMonth(current.getMonth() + intervalMonths);
  }

  return count;
}
